import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { LoggingService } from 'src/app/services/logging/logging.service';

/** Which product's messages a hit comes from; the callable defaults to connect only. */
export type SearchSource = 'connect' | 'chat';

export interface SearchResult {
  messageId: string;
  conversationId: string;
  source?: SearchSource;
  /** Chat hits: the chat to open (also mirrored in `conversationId`). */
  chatId?: string;
  content: string;
  contentPlain: string;
  sender: string;
  senderName: string;
  channelName: string;
  conversationName: string;
  createdOn: number;
  highlights: any[];
}

export interface SearchResponse {
  results: SearchResult[];
  totalFound: number;
  page: number;
}

export interface SearchOptions {
  conversationId?: string;
  page?: number;
  perPage?: number;
  /** Products to search; omitted = connect only (the callable's default). */
  sources?: SearchSource[];
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  private query$ = new Subject<{ query: string; options: SearchOptions }>();
  private resultsSubject = new BehaviorSubject<SearchResponse>({ results: [], totalFound: 0, page: 1 });
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public results$: Observable<SearchResponse> = this.resultsSubject.asObservable();
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  // Simple result cache
  private cache = new Map<string, { response: SearchResponse; timestamp: number }>();
  private readonly CACHE_TTL = 60000; // 1 minute
  private readonly CACHE_MAX_SIZE = 50;

  constructor(
    private firebase: FirebaseService,
    private logging: LoggingService,
  ) {
    this.query$.pipe(
      debounceTime(300),
      distinctUntilChanged((prev, curr) =>
        prev.query === curr.query
        && prev.options.conversationId === curr.options.conversationId
        && prev.options.page === curr.options.page
        && (prev.options.sources ?? []).join() === (curr.options.sources ?? []).join(),
      ),
      switchMap(({ query, options }) =>
        from(this.executeSearch(query, options)).pipe(
          catchError((error) => {
            this.logging.error('SearchService: search error', error);
            return of({ results: [], totalFound: 0, page: options.page || 1 } as SearchResponse);
          }),
        ),
      ),
    ).subscribe((response) => {
      this.resultsSubject.next(response);
      this.loadingSubject.next(false);
    });
  }

  public search(query: string, options: SearchOptions = {}): void {
    if (!query || query.trim().length === 0) {
      this.resultsSubject.next({ results: [], totalFound: 0, page: 1 });
      return;
    }

    this.loadingSubject.next(true);
    this.query$.next({ query: query.trim(), options });
  }

  public clearResults(): void {
    this.resultsSubject.next({ results: [], totalFound: 0, page: 1 });
  }

  public clearCache(): void {
    this.cache.clear();
  }

  private pruneCache(): void {
    const now = Date.now();
    for (const [key, val] of this.cache) {
      if (now - val.timestamp > this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }
    // If still over max size, remove oldest entries
    if (this.cache.size > this.CACHE_MAX_SIZE) {
      const entries = [...this.cache.entries()].sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, this.cache.size - this.CACHE_MAX_SIZE);
      toRemove.forEach(([key]) => this.cache.delete(key));
    }
  }

  private async executeSearch(query: string, options: SearchOptions): Promise<SearchResponse> {
    const cacheKey = `${query}|${options.conversationId || ''}|${options.page || 1}|${(options.sources ?? []).join(',')}`;
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.response;
    }

    try {
      const result = await this.firebase.functions.call('searchMessages', {
        query,
        conversationId: options.conversationId,
        page: options.page || 1,
        perPage: options.perPage || 20,
        ...(options.sources?.length ? { sources: options.sources } : {}),
      });

      const response: SearchResponse = {
        results: result.results || [],
        totalFound: result.totalFound || 0,
        page: result.page || 1,
      };

      this.cache.set(cacheKey, { response, timestamp: Date.now() });
      this.pruneCache();
      return response;
    } catch (error) {
      this.logging.error('SearchService: Cloud Function call failed', error);
      return { results: [], totalFound: 0, page: options.page || 1 };
    }
  }
}
